import { z } from 'zod';
/**
 * The onboarding workbook importer (D-155b, Batch 5). ONE downloadable .xlsx template with four tabs
 * (Categories, Suppliers, Contracts, Spend); the user fills it and uploads it once. Every row resolves
 * against Master Data by name (case-insensitive) or auto-creates the master row, so nothing imports
 * unlinked. Dry-run preview first (BP-21 pattern), then an audited, per-tab-transactional commit.
 * The API accepts the whole workbook (base64) or individual CSVs per tab.
 */
export const importTabSchema = z.enum(['categories', 'suppliers', 'contracts', 'spend']);
/** POST /import/preview + /import/commit — one workbook OR up to four per-tab CSV texts. */
export const importRequestSchema = z
    .object({
    /** The filled .xlsx workbook, base64-encoded (max ~14 MB decoded). */
    workbookBase64: z.string().max(20_000_000).optional(),
    /** Alternative: individual CSV files, one per tab. */
    csv: z
        .array(z.object({ tab: importTabSchema, text: z.string().max(5_000_000) }).strict())
        .max(4)
        .optional(),
})
    .strict()
    .refine((v) => Boolean(v.workbookBase64) !== Boolean(v.csv && v.csv.length > 0), {
    message: 'Send either workbookBase64 or csv, not both and not neither',
});
export const importRowIssueSchema = z.object({
    /** 1-based data row number (the header row is row 0 of the sheet and not counted). */
    row: z.number(),
    name: z.string().optional(),
    reason: z.string(),
});
export const importTabReportSchema = z.object({
    tab: importTabSchema,
    /** Non-blank data rows found on the tab. */
    rows: z.number(),
    /** Rows that will insert (preview) / did insert (commit). */
    toImport: z.number(),
    skips: z.array(importRowIssueSchema),
    errors: z.array(importRowIssueSchema),
    /** Master rows the linking rules will create / created (names). */
    createsSuppliers: z.array(z.string()),
    createsCategories: z.array(z.string()),
});
export const importPreviewResponseSchema = z.object({
    tabs: z.array(importTabReportSchema),
    /** Honest, human-readable notes (e.g. re-uploading duplicates contract/spend rows). */
    notes: z.array(z.string()),
});
export const importCommitResponseSchema = z.object({
    tabs: z.array(importTabReportSchema),
    createdSuppliers: z.number(),
    createdCategories: z.number(),
    insertedTotal: z.number(),
});
//# sourceMappingURL=import.js.map